/**
 * Compute shipping subtotal from cart items (excluding digital products and gift items).
 * Matches useCartShipping logic for consistency with free shipping/gift threshold checks.
 *
 * Only includes physical products that:
 * - require shipping
 * - are not digital products (patterns or gift cards)
 * - are not gift items (items with _pickedReward property)
 */
export function computeShippingSubtotal(items: ShopifyCartItem[]): number {
  if (!items?.length) return 0;

  return items.reduce((total, item) => {
    // Cart API may not include product.metafields; use requires_shipping as fallback for patterns
    // Gift cards are digital (excluded from shipping)
    const isGiftCard = !!(item.gift_card || item.product?.gift_card);
    const isDigitalProduct =
      item.product?.metafields?.hobbii?.entity_type === 'pattern' ||
      isGiftCard ||
      (item.requires_shipping === false && !isGiftCard);
    const isGiftItem = item.properties?._pickedReward === 'true';

    if (item.requires_shipping && !isDigitalProduct && !isGiftItem) {
      return total + (item.final_line_price || 0);
    }
    return total;
  }, 0);
}

/**
 * Identifier for a line item in cart/update.js. Prefer `key` so lines that share a variant_id
 * (e.g. gift with _pickedReward vs regular) are updated correctly.
 */
export function getLineItemUpdateKey(item: ShopifyCartItem): string {
  if (item.key) return item.key;
  if (item.variant_id != null) return String(item.variant_id);
  return String(item.id);
}

/**
 * Find the 1-based line number of the cart item with _pickedReward, or null if none.
 */
export function findPickedRewardLineNumber(
  items: ShopifyCartItem[]
): number | null {
  if (!items?.length) return null;
  const index = items.findIndex(
    (item) => item.properties?._pickedReward === 'true'
  );
  return index >= 0 ? index + 1 : null;
}

/**
 * Read gift threshold (cents) from cart DOM when store has not yet been set by initializers.
 * Tries .js-threshold-visualizer-mount then .js-gift-collection-display-mount (same value in Liquid).
 * Used so cartChangeQty can remove _pickedReward even if a quantity change happens before
 * initThresholdVisualizer's onBeforeMount runs.
 */
export function getGiftThresholdFromDOM(): number {
  const el =
    document.querySelector<HTMLElement>('.js-threshold-visualizer-mount') ??
    document.querySelector<HTMLElement>('.js-gift-collection-display-mount');
  if (!el?.dataset.giftThreshold) return 0;
  const n = Number(el.dataset.giftThreshold);
  return Number.isFinite(n) && n > 0 ? n : 0;
}
